import { useState } from 'react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { CircleAlert, Pencil, Plus, RefreshCw } from 'lucide-react'
import {
  getV1MeRetrieveQueryOptions,
  getV1QuizAdminTopicsListQueryKey,
  useV1QuizAdminCapabilities,
  useV1QuizAdminTopicsCreate,
  useV1QuizAdminTopicsList,
  useV1QuizAdminTopicsPartialUpdate,
} from '@/api/client'
import type { AdminQuizTopic } from '@/api/model'
import { PageHeader } from '@/components/page-header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { DataTable, type Column } from '@/components/data-table'
import { TopicDialog } from '@/components/quiz-bank/topic-dialog'
import { questionsNoun } from '@/lib/quiz-labels'
import { QuizCapabilityNotice } from '@/components/quiz/quiz-capability-notice'
import { canAuthorQuiz, quizCapabilitiesOrClosed } from '@/components/quiz/quiz-capabilities'
import { queryHasHttpError } from '@/lib/query-state'

export const Route = createFileRoute('/_panel/tematy/')({
  beforeLoad: async ({ context }) => {
    // Ta sama brama co bank pytań — słownik jest jego częścią, nie osobnym
    // obszarem uprawnień.
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && me.data.role !== 'admin' && me.data.role !== 'superadmin') {
      throw redirect({ to: '/' })
    }
  },
  component: TopicsPage,
})

const PAGE_SIZE = 25

/**
 * Słownik tematów pytań (spec 088).
 *
 * Jedyne miejsce, w którym temat POWSTAJE — formularz pytania pozwala go tylko
 * wybrać. Dzięki temu literówkę da się popełnić raz i widać ją tutaj, obok
 * poprawnego sąsiada, a poprawka nazwy idzie za wszystkimi pytaniami naraz.
 *
 * Nie ma usuwania: temat bywa już kotwicą w treści aplikacji i w historycznych
 * sesjach. Wycofanie to `is_active=false` — pozycja znika z pickera przy
 * pytaniu, a przypisania zostają.
 */
function TopicsPage() {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<AdminQuizTopic | null>(null)
  const [creating, setCreating] = useState(false)
  const capabilitiesQuery = useV1QuizAdminCapabilities()
  const capabilities = quizCapabilitiesOrClosed(
    capabilitiesQuery.data?.status === 200 ? capabilitiesQuery.data.data : undefined,
  )
  const canAuthor = canAuthorQuiz(capabilities)

  const query = useV1QuizAdminTopicsList({
    page,
    page_size: PAGE_SIZE,
    search: search.trim() || undefined,
  })
  const data = query.data?.status === 200 ? query.data.data : undefined
  const failed = query.isError || (query.data !== undefined && query.data.status !== 200)

  const createMutation = useV1QuizAdminTopicsCreate()
  const updateMutation = useV1QuizAdminTopicsPartialUpdate()

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: getV1QuizAdminTopicsListQueryKey() })

  const columns: Column<AdminQuizTopic>[] = [
    { header: 'Nazwa', cell: (topic) => <span className="font-medium">{topic.name}</span> },
    {
      header: 'Klucz',
      cell: (topic) => <code className="text-xs text-muted-foreground">{topic.slug}</code>,
    },
    {
      header: 'Pytania',
      // Dwa liczniki, bo dwa odczyty: powtórka kontekstowa bierze wyłącznie
      // tematy główne, agregacja i quiz tematyczny — wszystkie przypisania.
      cell: (topic) => (
        <span className="whitespace-nowrap text-muted-foreground">
          {topic.question_count} {questionsNoun(topic.question_count)}
          {topic.primary_question_count > 0 && (
            <span className="text-xs"> ({topic.primary_question_count} jako główny)</span>
          )}
        </span>
      ),
    },
    {
      header: 'W treści',
      cell: (topic) =>
        topic.is_glossary_term ? (
          <Badge variant="secondary">Pojęcie</Badge>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      header: 'Stan',
      cell: (topic) =>
        topic.is_active === false ? (
          <Badge variant="outline">Wycofany</Badge>
        ) : (
          <Badge variant="secondary">Aktywny</Badge>
        ),
    },
    {
      header: '',
      cell: (topic) => (
        <Button
          variant="ghost"
          size="sm"
          disabled={!canAuthor}
          title={!canAuthor ? 'Autorowanie quizów jest wyłączone na serwerze.' : undefined}
          onClick={() => setEditing(topic)}
        >
          <Pencil /> Edytuj
        </Button>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Słownik tematów"
        actions={
          <Button
            size="sm"
            disabled={!canAuthor}
            title={!canAuthor ? 'Autorowanie quizów jest wyłączone na serwerze.' : undefined}
            onClick={() => setCreating(true)}
          >
            <Plus /> Nowy temat
          </Button>
        }
      />

      <div className="flex flex-col gap-4 p-6">
        <QuizCapabilityNotice capabilities={capabilities} />
        <p className="text-sm text-muted-foreground">
          Tematy grupują pytania i wyznaczają, co dostanie uczeń po stuknięciu w pojęcie w treści
          aplikacji. Klucz składa się z nazwy i po utworzeniu jest niezmienny — to on jest kotwicą w
          treści; zmieniać można nazwę.
        </p>

        {failed ? (
          <Card>
            <CardContent className="flex items-center gap-3 py-6">
              <CircleAlert className="size-4 text-destructive" />
              <span className="text-sm">Nie udało się wczytać słownika.</span>
              <Button variant="outline" size="sm" onClick={() => query.refetch()}>
                <RefreshCw /> Spróbuj ponownie
              </Button>
            </CardContent>
          </Card>
        ) : (
          <DataTable
            columns={columns}
            rows={data?.results ?? []}
            rowKey={(topic) => topic.slug}
            count={data?.count ?? 0}
            page={page}
            onPageChange={setPage}
            pageSize={PAGE_SIZE}
            search={search}
            onSearchChange={(value) => {
              setSearch(value)
              setPage(1)
            }}
            searchPlaceholder="Szukaj tematu"
            isLoading={query.isLoading}
            isError={queryHasHttpError(query)}
            onRetry={() => void query.refetch()}
            emptyMessage="Słownik jest pusty."
            countNoun={['temat', 'tematów']}
          />
        )}
      </div>

      <TopicDialog
        open={creating}
        onOpenChange={setCreating}
        pending={createMutation.isPending}
        onSubmit={async (payload) => {
          const response = await createMutation.mutateAsync({ data: payload })
          if (response.status < 400) await refresh()
          return response
        }}
      />

      <TopicDialog
        key={editing?.slug}
        topic={editing ?? undefined}
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        pending={updateMutation.isPending}
        onSubmit={async (payload) => {
          const response = await updateMutation.mutateAsync({
            slug: editing!.slug,
            data: payload,
          })
          if (response.status < 400) await refresh()
          return response
        }}
      />
    </>
  )
}
